import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import NotificationCenter from './NotificationCenter';
import supabase from '../supabase/supabase';
import { usePermissions } from '../hooks/usePermissions';
import { PERMISSIONS } from '../data/permissions';

const { FiMenu, FiX, FiUser, FiSettings, FiLogOut, FiChevronDown, FiShield, FiUsers } = FiIcons;

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAccountOpen, setIsAccountOpen] = useState(false);
  const [session, setSession] = useState(null);
  const { canSystem, isInternalStaff, loading: permLoading } = usePermissions();
  
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });
    return () => subscription.unsubscribe();
  }, []);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  const navigation = [
    { name: 'Home', href: '/', public: true },
    { name: 'Assessments', href: '/assessments', public: true },
    { name: 'Dashboard', href: '/dashboard', public: false },
    { name: 'My Team', href: '/team', public: false }
  ];

  const visibleNavigation = navigation.filter(item => item.public || !!session);

  return (
    <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-[#0758D1] rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-lg">BA</span>
            </div>
            <span className="text-xl font-bold text-[#333333] hidden sm:block font-ubuntu">Business 360°</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-1 lg:space-x-4">
            {visibleNavigation.map((item) => (
              <Link 
                key={item.name} 
                to={item.href}
                className={`px-3 py-2 rounded-lg text-sm font-bold transition-all ${location.pathname === item.href ? 'text-[#0758D1] bg-blue-50' : 'text-gray-500 hover:text-[#0758D1] hover:bg-gray-50'}`}
              >
                {item.name}
              </Link>
            ))}
          </nav>

          {/* Right side icons */}
          <div className="flex items-center space-x-1 md:space-x-3">
            {session && !permLoading && isInternalStaff() && (
              <Link 
                to="/admin/catalog" 
                className="hidden lg:flex items-center gap-2 px-3 py-1.5 bg-slate-900 text-white rounded-lg text-xs font-bold hover:bg-slate-800 transition-all"
              >
                <SafeIcon icon={FiShield} className="w-3 h-3" /> Admin Panel
              </Link>
            )}

            {session && <NotificationCenter />}

            {session ? (
              <div className="relative">
                <button 
                  onClick={() => setIsAccountOpen(!isAccountOpen)}
                  className="flex items-center gap-2 p-1.5 hover:bg-gray-50 rounded-xl transition-all border border-transparent hover:border-gray-100"
                >
                  <div className="w-8 h-8 bg-[#6F34A9] rounded-lg flex items-center justify-center text-white">
                    <SafeIcon icon={FiUser} className="w-4 h-4" />
                  </div>
                  <SafeIcon icon={FiChevronDown} className={`text-gray-400 transition-transform ${isAccountOpen ? 'rotate-180' : ''}`} />
                </button>

                <AnimatePresence>
                  {isAccountOpen && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setIsAccountOpen(false)}></div>
                      <motion.div 
                        initial={{ opacity: 0, y: 10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 10, scale: 0.95 }}
                        className="absolute right-0 mt-2 w-56 bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 overflow-hidden"
                      >
                        <div className="p-4 border-b border-gray-50 bg-gray-50/50">
                          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Signed in as</p>
                          <p className="text-sm font-bold text-[#333333] truncate">{session.user.email}</p>
                        </div>
                        <div className="p-2">
                          {isInternalStaff() && (
                            <Link 
                              to="/admin/users" 
                              onClick={() => setIsAccountOpen(false)}
                              className="flex items-center gap-3 px-4 py-2.5 text-sm font-bold text-gray-600 hover:text-[#0758D1] hover:bg-blue-50 rounded-xl transition-all"
                            >
                              <SafeIcon icon={FiUsers} /> User Management
                            </Link>
                          )}
                          <Link 
                            to="/team" 
                            onClick={() => setIsAccountOpen(false)}
                            className="flex items-center gap-3 px-4 py-2.5 text-sm font-bold text-gray-600 hover:text-[#0758D1] hover:bg-blue-50 rounded-xl transition-all"
                          >
                            <SafeIcon icon={FiSettings} /> Account Settings
                          </Link>
                          <button 
                            onClick={handleLogout}
                            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-bold text-red-500 hover:bg-red-50 rounded-xl transition-all text-left"
                          >
                            <SafeIcon icon={FiLogOut} /> Sign Out
                          </button>
                        </div>
                      </motion.div>
                    </>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <Link 
                to="/login" 
                className="bg-[#0758D1] text-white px-5 py-2 rounded-xl text-sm font-bold hover:bg-blue-700 transition-all shadow-md shadow-blue-100"
              >
                Sign In
              </Link>
            )}

            {/* Mobile menu button */}
            <button 
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <SafeIcon icon={isMenuOpen ? FiX : FiMenu} className="w-6 h-6" />
            </button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;